import { useCallback, useRef, useState } from 'react';
import { line, Size, dollarsToCents, t, type SuggestionRow } from '@bms2/shared';
import { Dropdown, NumericInput, Select, TextInput } from '../../components/controls';
import { AlertBanner } from '../../components/controls/AlertBanner';
import { Modal } from '../../components/modals/Modal';
import { apiFetch, post, useApi, useRows } from './api';
import { useEmploymentSearch } from './DealModals';
import { LEADS_PATH } from './LeadStageCards';
import styles from './RecordPage.module.css';
import { useAddNew } from './AddNewHost';

interface LeadModalProps {
  open: boolean;
  leadId: string;
  apiBaseUrl?: string;
  onCancel: () => void;
  onSaved: () => void;
}

/** Mark Not a Fit: the reason is required and non-blank, because a lead discarded without one teaches the intake nothing. */
export function NotAFitModal({ open, leadId, apiBaseUrl = '', onCancel, onSaved }: LeadModalProps) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const save = async () => {
    if (!reason.trim()) return setError(t('lead.notFit.needsReason'));
    const answer = await post(`${LEADS_PATH}/${leadId}/not-a-fit`, { reason }, apiBaseUrl);
    if (!answer.ok) return setError(answer.message);
    onSaved();
    onCancel();
  };
  return (
    <Modal open={open} title={t('lead.notFit.title')} onCancel={onCancel} dataModal="lead-not-fit" actions={[{ kind: 'primary', label: t('lead.notFit.confirm'), onAct: () => save() }, { kind: 'cancel', label: t('modal.cancel'), onAct: onCancel }]}>
      {error ? <AlertBanner tone="error" message={error} action={{ label: t('modal.dismiss'), onAction: () => setError(null) }} /> : null}
      <TextInput size={Size.Large} label={t('lead.notFit.reason')} value={reason} onChange={setReason} name="notFitReason" required />
    </Modal>
  );
}

/** Convert to Deal: the Lead's Negotiation complete, or the override once enough call attempts are logged, with a typed reason (decision 0254). */
export function ConvertToDealModal({ open, leadId, overrideOffered, negotiationComplete, apiBaseUrl = '', onCancel, onSaved }: LeadModalProps & { overrideOffered: boolean; negotiationComplete: boolean }) {
  const [name, setName] = useState('');
  const [dealType, setDealType] = useState<'Project' | 'Parts' | 'Service'>('Project');
  const [value, setValue] = useState('');
  const [override, setOverride] = useState('');
  const [error, setError] = useState<string | null>(null);
  const needsOverride = !negotiationComplete;
  const save = async () => {
    if (needsOverride && !overrideOffered) return setError(t('lead.convert.notOffered'));
    if (needsOverride && !override.trim()) return setError(t('lead.convert.needsReason'));
    const answer = await post<{ dealId: string }>(`${LEADS_PATH}/${leadId}/convert-to-deal`, { name: name.trim() || undefined, dealType, dealValue: value.trim() ? Number(value) : undefined, ...(needsOverride ? { overrideReason: override } : {}) }, apiBaseUrl);
    if (!answer.ok) return setError(answer.message);
    onSaved();
    onCancel();
  };
  return (
    <Modal open={open} title={t('lead.convert.title')} onCancel={onCancel} dataModal="lead-convert-deal" actions={[{ kind: 'primary', label: t('lead.convert.confirm'), onAct: () => save() }, { kind: 'cancel', label: t('modal.cancel'), onAct: onCancel }]}>
      {error ? <AlertBanner tone="error" message={error} action={{ label: t('modal.dismiss'), onAction: () => setError(null) }} /> : null}
      <TextInput size={Size.Medium} label={t('lead.convert.name')} value={name} onChange={setName} name="dealName" />
      <Select size={Size.Small} label={t('lead.convert.type')} value={dealType} onChange={(chosen) => setDealType(chosen as 'Project' | 'Parts' | 'Service')} options={(['Project', 'Parts', 'Service'] as const).map((type) => ({ value: type, label: type }))} dataAttribute={{ 'data-lead-field': 'dealType' }} />
      <NumericInput size={Size.Medium} label={t('lead.convert.value')} kind="currency" value={value.trim() === '' ? null : dollarsToCents(Number(value))} onChange={(next) => setValue(next === null ? '' : String(next / 100))} name="dealValue" />
      {needsOverride ? (
        <div data-lead-override={overrideOffered ? 'offered' : 'withheld'}>
          <p className={styles.hint}>{overrideOffered ? t('lead.convert.overrideHint') : t('lead.convert.notOffered')}</p>
          {overrideOffered ? <TextInput size={Size.Large} label={t('lead.convert.overrideReason')} value={override} onChange={setOverride} name="overrideReason" required /> : null}
        </div>
      ) : null}
    </Modal>
  );
}

/** Redirect: the one Lead moves, the boundary stays, no reason is asked for and from and to are recorded regardless (decision 0227). */
export function RedirectModal({ open, leadId, apiBaseUrl = '', onCancel, onSaved }: LeadModalProps) {
  const territories = useApi<{ rows: { id: string; name: string }[] }>(open ? `${LEADS_PATH}/territories` : null, apiBaseUrl);
  const [territoryId, setTerritoryId] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const rows = territories.state.kind === 'ready' ? territories.state.data.rows : [];
  const save = async () => {
    const answer = await post(`${LEADS_PATH}/${leadId}/redirect`, { territoryId: territoryId || null, reason: reason.trim() || null }, apiBaseUrl);
    if (!answer.ok) return setError(answer.message);
    onSaved();
    onCancel();
  };
  return (
    <Modal open={open} title={t('lead.redirect.title')} onCancel={onCancel} dataModal="lead-redirect" actions={[{ kind: 'primary', label: t('lead.redirect.confirm'), onAct: () => save() }, { kind: 'cancel', label: t('modal.cancel'), onAct: onCancel }]}>
      {error ? <AlertBanner tone="error" message={error} action={{ label: t('modal.dismiss'), onAction: () => setError(null) }} /> : null}
      <Select size={Size.Large} label={t('lead.redirect.territory')} value={territoryId} onChange={setTerritoryId} placeholder={t('lead.redirect.general')} options={rows.map((r) => ({ value: r.id, label: r.name }))} dataAttribute={{ 'data-lead-field': 'territory' }} />
      <TextInput size={Size.Large} label={t('lead.redirect.reason')} value={reason} onChange={setReason} name="redirectReason" />
    </Modal>
  );
}

/** Dispatch: the department head names the salesperson; the work item reaches that employment (decision 0253). */
export function DispatchModal({ open, leadId, apiBaseUrl = '', onCancel, onSaved }: LeadModalProps) {
  const addNew = useAddNew(apiBaseUrl);
  const search = useEmploymentSearch(apiBaseUrl, null);
  const [employmentId, setEmploymentId] = useState<string | null>(null);
  const [name, setName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const save = async () => {
    if (!employmentId) return setError(t('lead.dispatch.needsRep'));
    const answer = await post(`${LEADS_PATH}/${leadId}/dispatch`, { employmentId }, apiBaseUrl);
    if (!answer.ok) return setError(answer.message);
    onSaved();
    onCancel();
  };
  return (
    <Modal open={open} title={t('lead.dispatch.title')} onCancel={onCancel} dataModal="lead-dispatch" actions={[{ kind: 'primary', label: t('lead.dispatch.confirm'), onAct: () => save() }, { kind: 'cancel', label: t('modal.cancel'), onAct: onCancel }]}>
      {error ? <AlertBanner tone="error" message={error} action={{ label: t('modal.dismiss'), onAction: () => setError(null) }} /> : null}
      <div className={styles.field} data-lead-field="rep">
        <Dropdown size={Size.Medium} label={t('lead.dispatch.rep')} entityLabel={t('lead.dispatch.repEntity')} search={search} value={name} onSelect={(row: SuggestionRow) => { setEmploymentId(row.id); setName(row.line1); }} onAddNew={() => addNew.open({ kind: 'employee' }, (row: SuggestionRow) => { setEmploymentId(row.id); setName(row.line1); })} />
      </div>
      {addNew.host}
    </Modal>
  );
}

/** + Add Role on the Company's Account Roles tile: Sales Person writes the assigned-rep field, every other role a dated link row (piece 20). */
/**
 * Who the Add an account role picker offers for a role (decision 0670): the
 * qualifying job role's current holders, or everybody where the role names none.
 * 🔨 Relative to the API root.
 */
export function roleCandidatesPath(roleTypeId: string, query = ''): string {
  return `${LEADS_PATH}/role-types/${encodeURIComponent(roleTypeId)}/candidates${query ? `?q=${encodeURIComponent(query)}` : ''}`;
}

interface PanelRole {
  id: string;
  name: string;
  /** The job role a person's current position holds to be offered for this role; null offers everybody (decision 0670). */
  jobRoleId?: string | null;
  jobRoleName?: string | null;
}

interface RoleCandidates {
  jobRoleName: string | null;
  qualifying: number;
  rows: { id: string; label: string; jobRoleId: string | null }[];
}

/**
 * 🔒 Add an account role (piece 20). The Person picker offers only the current
 * employments whose position holds the chosen role's qualifying job role, and
 * everybody where the role names none (decision 0670; the owner's T88). Changing
 * the Role filters again and clears a picked person who no longer matches, and a
 * role nobody holds says so, naming the job role.
 */
export function AccountRoleModal({ open, companyId, apiBaseUrl = '', onCancel, onSaved }: { open: boolean; companyId: string; apiBaseUrl?: string; onCancel: () => void; onSaved: () => void }) {
  const addNew = useAddNew(apiBaseUrl);
  const roles = useApi<{ panel: PanelRole[] }>(open ? `${LEADS_PATH}/role-types` : null, apiBaseUrl);
  const [roleTypeId, setRoleTypeId] = useState('');
  const [employmentId, setEmploymentId] = useState<string | null>(null);
  const [name, setName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const panel = roles.state.kind === 'ready' ? roles.state.data.panel ?? [] : [];
  const role = panel.find((row) => row.id === roleTypeId) ?? null;
  // Before a role is chosen the picker searches as the first role, Sales Person, which names no job role and so offers everybody.
  const searchAs = roleTypeId || panel[0]?.id || '';
  /** The job role each offered person's current position holds, so a Role change can tell whether the pick still matches. */
  const heldJobRole = useRef(new Map<string, string | null>());
  const pickedJobRole = useRef<string | null>(null);
  const qualified = useApi<RoleCandidates>(open && role?.jobRoleId ? roleCandidatesPath(role.id) : null, apiBaseUrl);
  const search = useCallback(async (query: string, signal: AbortSignal): Promise<SuggestionRow[]> => {
    if (!searchAs) return [];
    const response = await apiFetch(`${apiBaseUrl}${roleCandidatesPath(searchAs, query)}`, { signal });
    if (!response.ok) return [];
    const body = (await response.json()) as Partial<RoleCandidates>;
    const rows = Array.isArray(body.rows) ? body.rows : [];
    for (const row of rows) heldJobRole.current.set(row.id, row.jobRoleId ?? null);
    return rows.map((row) => ({ id: row.id, line1: row.label }));
  }, [apiBaseUrl, searchAs]);
  const chooseRole = (next: string) => {
    setRoleTypeId(next);
    const nextRole = panel.find((row) => row.id === next) ?? null;
    // 🔒 A picked person who does not hold the new role's job role is cleared rather than carried into a refused save.
    if (employmentId && nextRole?.jobRoleId && pickedJobRole.current !== nextRole.jobRoleId) {
      setEmploymentId(null);
      setName(null);
      pickedJobRole.current = null;
    }
  };
  const save = async () => {
    if (!roleTypeId) return setError(t('lead.role.needsRole'));
    if (!employmentId) return setError(t('lead.role.needsPerson'));
    const answer = await post(`${LEADS_PATH}/companies/${companyId}/roles`, { roleTypeId, employmentId }, apiBaseUrl);
    if (!answer.ok) return setError(answer.message);
    onSaved();
    onCancel();
  };
  const noneQualify = role?.jobRoleId && qualified.state.kind === 'ready' && qualified.state.data.qualifying === 0;
  return (
    <Modal open={open} title={t('lead.role.title')} onCancel={onCancel} dataModal="account-role" actions={[{ kind: 'primary', label: t('lead.role.confirm'), onAct: () => save() }, { kind: 'cancel', label: t('modal.cancel'), onAct: onCancel }]}>
      {error ? <AlertBanner tone="error" message={error} action={{ label: t('modal.dismiss'), onAction: () => setError(null) }} /> : null}
      <Select size={Size.Medium} label={t('lead.role.role')} value={roleTypeId} onChange={chooseRole} placeholder={t('edit.none')} options={panel.map((r) => ({ value: r.id, label: r.name }))} dataAttribute={{ 'data-role-field': 'roleType' }} />
      {/* Keyed to the role, so a changed Role searches afresh rather than showing the last role's list. */}
      <div className={styles.field} data-role-field="person" data-role-job-role={role?.jobRoleId ?? ''}>
        <Dropdown key={searchAs} size={Size.Medium} label={t('lead.role.person')} entityLabel={t('lead.dispatch.repEntity')} search={search} value={name} onSelect={(row: SuggestionRow) => { setEmploymentId(row.id); setName(row.line1); pickedJobRole.current = heldJobRole.current.get(row.id) ?? null; }} onAddNew={() => addNew.open({ kind: 'employee' }, (row: SuggestionRow) => { setEmploymentId(row.id); setName(row.line1); pickedJobRole.current = heldJobRole.current.get(row.id) ?? null; })} />
      </div>
      {role?.jobRoleName ? (
        <p className={styles.hint} data-role-qualifying={noneQualify ? 'none' : 'some'}>
          {noneQualify ? line(t('lead.role.noneQualify'), role.jobRoleName) : line(t('lead.role.qualifying'), role.jobRoleName)}
        </p>
      ) : null}
      <p className={styles.hint}>{t('lead.role.hint')}</p>
      {addNew.host}
    </Modal>
  );
}

/** The held-leads queue's per-row reads, for the list page's Release column. */
export function useHeldReport(apiBaseUrl: string, open: boolean) {
  return useRows<{ signalKey: string; held: number; released: number; ratePercent: number; surfaced: boolean }>(open ? `${LEADS_PATH}/held-report` : null, apiBaseUrl);
}
